/**
 * collabClient — live editor↔editor collaboration over tile-pubsub-tunnel.
 *
 * TWO PLANES on the shared tunnel socket (the one presenceClient owns):
 *
 *  • CONTROL plane — rides the PRESENCE channel `presence:app:<id>` (everyone in
 *    the app is already there). Carries the join handshake (REQUEST_JOIN /
 *    JOIN_APPROVED / JOIN_DENIED / JOIN_READY), snapshots (SNAPSHOT), and the
 *    one-shot pull request (REQUEST_SNAPSHOT). It's always-on once initCollab()
 *    runs, so a host hears join requests and ANY peer can answer a pull —
 *    without being in a live session. Presence ignores unknown `.t` types, so
 *    the two protocols coexist on one channel.
 *
 *  • DATA plane — a dedicated channel `code:app:<id>`, joined ONLY while in a
 *    live session. Carries EDIT streaming + LOCK/UNLOCK (per-file single-writer)
 *    + CHELLO (roster). Echo-suppression mirrors devBridge: we hash every file
 *    we APPLY; when updateFiles re-runs with that content, shouldEmit sees the
 *    matching hash and skips re-broadcasting — an edit travels exactly once.
 *
 * JOIN MODEL — adopt-host (no symmetric merge, no clobber). A joiner REQUESTS;
 * the host APPROVES; both FREEZE while the host's snapshot transfers; the joiner
 * stashes its own pre-join buffer (a durable backup via the onBeforeAdopt seam)
 * then adopts the host's state WHOLESALE; JOIN_READY unfreezes both and
 * bidirectional streaming begins. The freeze window (sub-second) removes every
 * "what if someone edits mid-decision" race.
 *
 * PULL-ONCE — "reconcile with a peer, one time, no stream": request their
 * buffer, hand it back to the caller (EditorViewV2 runs the shared review
 * modal), apply. Join minus the streaming.
 *
 * React-free (mirrors presenceClient/openContext). UI reads via subscribeCollab.
 */
import { type Socket } from 'socket.io-client';

import {
  getTunnelSocket,
  getPresenceChannel,
} from '@/features/editor/collaboration/cursors/presence-client';
import { getBaseSaveId } from '@/features/editor/session/utils/save-base';

export type CollabRole = 'host' | 'guest';

/**
 * Session phase (state machine). Freeze/stream are derived from it:
 *   idle          — not in a session
 *   hosting       — host, streaming
 *   transferring  — host, FROZEN, snapshot sent, awaiting the joiner's JOIN_READY
 *   requesting    — guest, FROZEN, awaiting approve/deny
 *   applying      — guest, FROZEN, adopting the host snapshot
 *   guesting      — guest, streaming
 */
export type CollabPhase =
  | 'idle'
  | 'hosting'
  | 'transferring'
  | 'requesting'
  | 'applying'
  | 'guesting';

export interface CollabState {
  /** True while in a session at all (phase !== idle). */
  live: boolean;
  /** True while the editor must be read-only (handshake in flight). */
  frozen: boolean;
  phase: CollabPhase;
  role: CollabRole | null;
  /** Other participants currently streaming in the live session (ids). */
  participants: string[];
  /** Files currently locked by OTHER participants (single-writer). path → owner. */
  locks: Record<string, { ownerId: string; name: string }>;
  /** Host: a peer awaiting approval to join (one at a time in v1). */
  pendingJoin: { id: string; name: string } | null;
  /** Transient status line for the guest UI (waiting / syncing / declined). */
  statusText: string | null;
}

// ── Wire messages ─────────────────────────────────────────────────────────────
// Data plane (code channel):
/** A file edit. `contents: null` ⇒ delete/unlink. */
interface EditMsg {
  t: 'EDIT';
  path: string;
  contents: string | null;
  fileType: string;
}
interface HelloMsg {
  t: 'CHELLO';
}
interface LockMsg {
  t: 'LOCK';
  path: string;
  name: string;
}
interface UnlockMsg {
  t: 'UNLOCK';
  path: string;
}
type DataMsg = EditMsg | HelloMsg | LockMsg | UnlockMsg;

// Control plane (presence channel):
interface ReqJoinMsg {
  t: 'REQUEST_JOIN';
  name: string;
}
interface JoinApprovedMsg {
  t: 'JOIN_APPROVED';
  to: string;
}
interface JoinDeniedMsg {
  t: 'JOIN_DENIED';
  to: string;
}
interface JoinReadyMsg {
  t: 'JOIN_READY';
  to: string;
}
/** A full code buffer. `kind` routes it: 'join' (host→joiner, adopt) vs 'pull'
 *  (any peer→puller, one-shot reconcile). */
interface SnapshotMsg {
  t: 'SNAPSHOT';
  to: string;
  kind: 'join' | 'pull';
  files: Record<string, { type: string; contents: string }>;
  /** The sender's live-buffer ASSET pointers ({type:'ASSET', contents:url}).
   *  Cheap URL strings, so they ride the wire; the receiver unions these with
   *  the assets materialized from `baseSaveId` (see setOnMergeAssets). */
  assets?: Record<string, { type: string; contents: string }>;
  baseSaveId: string | null;
}
interface ReqSnapshotMsg {
  t: 'REQUEST_SNAPSHOT';
  to: string;
}
type CtlMsg =
  | ReqJoinMsg
  | JoinApprovedMsg
  | JoinDeniedMsg
  | JoinReadyMsg
  | SnapshotMsg
  | ReqSnapshotMsg;

// ── Public seam types ─────────────────────────────────────────────────────────
export type FileMap = Record<string, { type: string; contents: string } | null>;
/** How a remote edit / adopted snapshot is applied into the editor buffer. */
export type ApplyFn = (updates: Record<string, { type: string; contents: string } | null>) => void;
/** Read the current editor buffer (code files only). */
export type GetFilesFn = () => Record<string, { type: string; contents: string }>;
/** Result of a one-shot pull: the peer's buffer + the save it's based on. */
export interface PulledSnapshot {
  files: Record<string, { type: string; contents: string }>;
  /** Peer's live-buffer ASSET pointers (see SnapshotMsg.assets). */
  assets: Record<string, { type: string; contents: string }>;
  baseSaveId: string | null;
}

// ── Tunables ──────────────────────────────────────────────────────────────────
const LOCK_STALE_MS = 10000; // a peer's lock expires if not refreshed within this
const LOCK_IDLE_MS = 8000; // release my lock after this long without editing it
const LOCK_REFRESH_MS = 4000; // re-broadcast a held lock at most this often
const EDIT_DEBOUNCE_MS = 200;
const REQUEST_RETRY_MS = 1500; // re-send REQUEST_JOIN until approved/denied
const REQUEST_TIMEOUT_MS = 20000; // give up waiting for the host to answer
const TRANSFER_TIMEOUT_MS = 15000; // host unfreezes if JOIN_READY never lands
const PULL_RETRY_MS = 1500;
const PULL_TIMEOUT_MS = 12000;

/** Signature App passes to shouldEmit for a deleted file (echo-guard parity). */
export const DELETE_SIG = '<<tile-deleted>>';

/** cyrb53 — same fast stable hash devBridge uses for echo-suppression. */
function cyrb53(str: string, seed = 0): string {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16);
}

// ── Module state ────────────────────────────────────────────────────────────────
type Listener = (state: CollabState) => void;
const listeners = new Set<Listener>();

/** Identity + app, set by initCollab; reused by both planes. */
let collabAppId: string | null = null;
let selfId: string | null = null;
let selfName = 'Someone';

let phase: CollabPhase = 'idle';
let pendingJoin: { id: string; name: string } | null = null;
let statusText: string | null = null;
/** Guest: the host we're joining (targets JOIN_READY). */
let hostId: string | null = null;

// Control plane (presence channel).
let ctlSocket: Socket | null = null;
let ctlHandler: ((data: { channel?: string; sender: string; message: unknown }) => void) | null =
  null;
let ctlAttached = false;
let ctlAttachRetry: ReturnType<typeof setInterval> | null = null;

// Data plane (code channel).
let dataSocket: Socket | null = null;
let codeChannel: string | null = null;
let onMessageHandler:
  | ((data: { channel?: string; sender: string; message: unknown }) => void)
  | null = null;
let onJoinHandler: ((data: { channel?: string; sender: string }) => void) | null = null;
let onLeaveHandler: ((data: { channel?: string; sender: string }) => void) | null = null;
const participants = new Set<string>();
let sweepTimer: ReturnType<typeof setInterval> | null = null;

/** Hash of the last content we APPLIED from a peer, per path — echo guard. */
const lastApplied = new Map<string, string>();
/** Per-path debounce timers for coalesced sends. */
const pending = new Map<string, ReturnType<typeof setTimeout>>();
/** Files locked by OTHER peers: path → { ownerId, name, ts }. */
const peerLocks = new Map<string, { ownerId: string; name: string; ts: number }>();
/** Files I hold: path → { lastEdit, lastSent }. */
const myLocks = new Map<string, { lastEdit: number; lastSent: number }>();

// Handshake / pull timers.
let requestRetry: ReturnType<typeof setInterval> | null = null;
let requestTimeout: ReturnType<typeof setTimeout> | null = null;
let transferTimeout: ReturnType<typeof setTimeout> | null = null;
let pullResolver: ((s: PulledSnapshot | null) => void) | null = null;
let pullRetry: ReturnType<typeof setInterval> | null = null;
let pullTimeout: ReturnType<typeof setTimeout> | null = null;

// Seams (wired by App.tsx / EditorViewV2).
let applyFn: ApplyFn | null = null;
let getFilesFn: GetFilesFn | null = null;
/** Read the current buffer's ASSET pointer entries (URL strings, not bytes).
 *  Kept separate from getFilesFn (code-only, shared with draft) so the snapshot
 *  can carry assets without perturbing draft's base-hash reads. */
let getAssetsFn: (() => Record<string, { type: string; contents: string }>) | null = null;
/** Apply a peer's ASSET pointers into the local buffer verbatim (they're the
 *  proven-working pointers the peer already renders). Wired to EditorViewV2. */
let onMergeAssets:
  | ((wireAssets: Record<string, { type: string; contents: string }>) => void)
  | null = null;
/** Called on the joiner right before adopting the host with the door-choice:
 *  stash=true → keep my pre-join work as a draft (restorable after leaving),
 *  stash=false → discard it. Wired to draft.backupPrejoin / draft.clearPrejoin. */
let onBeforeAdopt: ((stash: boolean) => void) | null = null;
/** The joiner's door choice: stash pre-join work as a draft, or discard it. */
let stashOnAdopt = false;

// ── Derived predicates ──────────────────────────────────────────────────────────
function canStream(): boolean {
  return phase === 'hosting' || phase === 'guesting';
}
function onCodeChannel(): boolean {
  return phase === 'hosting' || phase === 'transferring' || phase === 'guesting';
}
function isFrozen(): boolean {
  return phase === 'requesting' || phase === 'applying' || phase === 'transferring';
}
function currentRole(): CollabRole | null {
  if (phase === 'hosting' || phase === 'transferring') return 'host';
  if (phase === 'requesting' || phase === 'applying' || phase === 'guesting') return 'guest';
  return null;
}

// ── State broadcast ───────────────────────────────────────────────────────────
function snapshot(): CollabState {
  const locks: Record<string, { ownerId: string; name: string }> = {};
  const now = Date.now();
  for (const [path, l] of peerLocks) {
    if (now - l.ts < LOCK_STALE_MS) locks[path] = { ownerId: l.ownerId, name: l.name };
  }
  return {
    live: phase !== 'idle',
    frozen: isFrozen(),
    phase,
    role: currentRole(),
    participants: [...participants],
    locks,
    pendingJoin,
    statusText,
  };
}

function emitState(): void {
  const s = snapshot();
  for (const l of listeners) {
    try {
      l(s);
    } catch {
      /* one listener can't break the others */
    }
  }
}

// ── Send helpers ──────────────────────────────────────────────────────────────
/** Send a control message on the presence channel (always-on). */
function sendCtl(message: CtlMsg): void {
  const s = getTunnelSocket();
  const ch = getPresenceChannel();
  if (!s || !ch || !selfId) return;
  s.emit('message', { channel: ch, sender: selfId, message });
}

/** Send a data-plane message on the code channel (live session only). */
function sendCode(message: DataMsg): void {
  if (!onCodeChannel() || !dataSocket || !codeChannel || !selfId) return;
  dataSocket.emit('message', { channel: codeChannel, sender: selfId, message });
}

/** Read the current buffer as a code file map (for snapshots). */
function readFiles(): Record<string, { type: string; contents: string }> {
  return getFilesFn ? getFilesFn() : {};
}

/** Read the current buffer's ASSET pointers (for snapshots). */
function readAssets(): Record<string, { type: string; contents: string }> {
  return getAssetsFn ? getAssetsFn() : {};
}

// ── Apply / echo-suppression ────────────────────────────────────────────────────
/** Apply a set of files, recording echo-guard hashes so they don't bounce back. */
function applyIncoming(updates: Record<string, { type: string; contents: string } | null>): void {
  if (!applyFn) return;
  for (const [p, f] of Object.entries(updates)) {
    lastApplied.set(p, cyrb53(f == null ? DELETE_SIG : f.contents));
  }
  applyFn(updates);
}

/** Adopt the host's buffer WHOLESALE: overwrite matching files, delete the ones
 *  the host doesn't have. The joiner's pre-join buffer is stashed first (seam),
 *  so this is safe. Echo hashes are recorded so adopted files don't bounce once
 *  streaming starts. */
function adoptHost(
  hostFiles: Record<string, { type: string; contents: string }>,
  hostAssets: Record<string, { type: string; contents: string }>,
): void {
  if (onBeforeAdopt) {
    try {
      onBeforeAdopt(stashOnAdopt); // stash-as-draft or discard, per the door choice
    } catch {
      /* backup is best-effort; adoption proceeds regardless */
    }
  }
  // Assets FIRST: the host's ASSET pointers are copied verbatim into the buffer
  // BEFORE the code, so a `require('./assets/x.png')` in the adopted code always
  // finds a registered asset (else the preview throws "Unable to resolve module
  // …/x.png.js"). Additive — no wholesale delete of assets the host lacks.
  if (onMergeAssets) {
    try {
      onMergeAssets(hostAssets);
    } catch {
      /* asset merge is best-effort; code adoption proceeds regardless */
    }
  }
  const mine = readFiles();
  const updates: Record<string, { type: string; contents: string } | null> = {};
  for (const [p, f] of Object.entries(hostFiles)) {
    updates[p] = { type: f.type || 'CODE', contents: f.contents };
  }
  for (const p of Object.keys(mine)) {
    if (!(p in hostFiles)) updates[p] = null; // host doesn't have it → remove locally
  }
  if (Object.keys(updates).length) applyIncoming(updates);
}

// ── Control-plane handling ──────────────────────────────────────────────────────
function clearRequestTimers(): void {
  if (requestRetry) {
    clearInterval(requestRetry);
    requestRetry = null;
  }
  if (requestTimeout) {
    clearTimeout(requestTimeout);
    requestTimeout = null;
  }
}
function clearPullTimers(): void {
  if (pullRetry) {
    clearInterval(pullRetry);
    pullRetry = null;
  }
  if (pullTimeout) {
    clearTimeout(pullTimeout);
    pullTimeout = null;
  }
}

/** Guest: adopt the host snapshot, subscribe to the code channel, start streaming. */
function enterGuesting(): void {
  phase = 'guesting';
  openCodeChannel();
  if (hostId) sendCtl({ t: 'JOIN_READY', to: hostId });
  statusText = null;
  emitState();
}

function handleCtl(sender: string, message: unknown): void {
  const msg = message as CtlMsg;
  if (!msg || typeof msg !== 'object' || typeof (msg as { t?: unknown }).t !== 'string') return;

  switch (msg.t) {
    case 'REQUEST_JOIN': {
      // Accept a request while HOSTING (add a participant) OR while IDLE —
      // approving implicitly makes us the host, so "Go live" is no longer a
      // separate step. Never mid-flow (requesting/applying/transferring/guesting).
      if (phase !== 'hosting' && phase !== 'idle') return;
      if (pendingJoin && pendingJoin.id !== sender) return; // busy with another
      pendingJoin = { id: sender, name: msg.name || 'Someone' };
      emitState();
      break;
    }
    case 'JOIN_APPROVED': {
      if (msg.to !== selfId || phase !== 'requesting') return;
      statusText = 'Syncing…';
      clearRequestTimers();
      emitState();
      // The SNAPSHOT follows (or already arrived) — adoption happens there.
      break;
    }
    case 'JOIN_DENIED': {
      if (msg.to !== selfId || phase !== 'requesting') return;
      clearRequestTimers();
      phase = 'idle';
      hostId = null;
      statusText = 'Request declined';
      emitState();
      setTimeout(() => {
        if (phase === 'idle') {
          statusText = null;
          emitState();
        }
      }, 3000);
      break;
    }
    case 'SNAPSHOT': {
      if (msg.to !== selfId) return;
      if (msg.kind === 'join') {
        if (phase === 'requesting' || phase === 'applying') {
          clearRequestTimers();
          phase = 'applying';
          emitState();
          adoptHost(msg.files || {}, msg.assets || {});
          enterGuesting();
        }
      } else if (msg.kind === 'pull') {
        if (pullResolver) {
          const resolve = pullResolver;
          pullResolver = null;
          clearPullTimers();
          resolve({
            files: msg.files || {},
            assets: msg.assets || {},
            baseSaveId: msg.baseSaveId ?? null,
          });
        }
      }
      break;
    }
    case 'REQUEST_SNAPSHOT': {
      // Anyone can answer a pull with their current buffer — no session needed.
      if (msg.to !== selfId) return;
      sendCtl({
        t: 'SNAPSHOT',
        to: sender,
        kind: 'pull',
        files: readFiles(),
        assets: readAssets(),
        baseSaveId: getBaseSaveId(),
      });
      break;
    }
    case 'JOIN_READY': {
      if (msg.to !== selfId || phase !== 'transferring') return;
      if (transferTimeout) {
        clearTimeout(transferTimeout);
        transferTimeout = null;
      }
      phase = 'hosting';
      if (pendingJoin && pendingJoin.id === sender) participants.add(sender);
      pendingJoin = null;
      emitState();
      break;
    }
  }
}

// ── Data-plane handling ──────────────────────────────────────────────────────────
function handleData(sender: string, message: unknown): void {
  const msg = message as DataMsg;
  if (!msg || typeof msg !== 'object') return;
  if (msg.t === 'EDIT') {
    if (!applyFn || typeof msg.path !== 'string' || !canStream()) return;
    lastApplied.set(msg.path, cyrb53(msg.contents == null ? DELETE_SIG : msg.contents));
    if (msg.contents == null) applyFn({ [msg.path]: null });
    else applyFn({ [msg.path]: { type: msg.fileType || 'CODE', contents: msg.contents } });
  } else if (msg.t === 'CHELLO') {
    if (sender && sender !== selfId) {
      const isNew = !participants.has(sender);
      participants.add(sender);
      if (isNew) sendCode({ t: 'CHELLO' }); // reply so the newcomer lists us
      for (const path of myLocks.keys()) sendCode({ t: 'LOCK', path, name: selfName });
      emitState();
    }
  } else if (msg.t === 'LOCK') {
    if (sender && sender !== selfId && typeof msg.path === 'string') {
      peerLocks.set(msg.path, { ownerId: sender, name: msg.name || 'Someone', ts: Date.now() });
      emitState();
    }
  } else if (msg.t === 'UNLOCK') {
    const l = peerLocks.get(msg.path);
    if (l && l.ownerId === sender) {
      peerLocks.delete(msg.path);
      emitState();
    }
  }
}

/** Sweep: expire stale peer locks; release my idle locks. */
function sweep(): void {
  const now = Date.now();
  let changed = false;
  for (const [path, l] of peerLocks) {
    if (now - l.ts >= LOCK_STALE_MS) {
      peerLocks.delete(path);
      changed = true;
    }
  }
  for (const [path, l] of myLocks) {
    if (now - l.lastEdit >= LOCK_IDLE_MS) {
      myLocks.delete(path);
      sendCode({ t: 'UNLOCK', path });
    }
  }
  if (changed) emitState();
}

// ── Code-channel lifecycle ──────────────────────────────────────────────────────
function openCodeChannel(): void {
  const s = getTunnelSocket();
  if (!s || !collabAppId || !selfId) return;
  dataSocket = s;
  codeChannel = `code:app:${collabAppId}`;
  lastApplied.clear();
  peerLocks.clear();
  myLocks.clear();
  if (!sweepTimer) sweepTimer = setInterval(sweep, 2000);

  onMessageHandler = (data) => {
    if (data?.channel !== codeChannel || data?.sender === selfId) return;
    handleData(data.sender, data.message);
  };
  onJoinHandler = (data) => {
    if (data?.channel !== codeChannel) return;
    handleData(data.sender, { t: 'CHELLO' });
  };
  onLeaveHandler = (data) => {
    if (data?.channel !== codeChannel) return;
    // If the HOST leaves the code channel (clicked Leave, or closed the tab) while
    // we're a GUEST, the whole session is over for us — tear down and surface a
    // note. The guest-leave draft modal fires from EditorViewV2 off the resulting
    // guesting→idle transition (same as a manual leave).
    if (data?.sender && data.sender === hostId && currentRole() === 'guest') {
      hostEndedSession();
      return;
    }
    let changed = false;
    if (data?.sender && participants.delete(data.sender)) changed = true;
    for (const [p, l] of peerLocks)
      if (l.ownerId === data.sender) {
        peerLocks.delete(p);
        changed = true;
      }
    if (changed) emitState();
  };
  s.on('message', onMessageHandler);
  s.on('joinChannel', onJoinHandler);
  s.on('leaveChannel', onLeaveHandler);

  const start = () => {
    if (!onCodeChannel() || !dataSocket || !codeChannel || !selfId) return;
    dataSocket.emit('subscribeChannel', { channel: codeChannel, sender: selfId });
    sendCode({ t: 'CHELLO' });
  };
  if (s.connected) start();
  else s.once('connect', start);
}

function closeCodeChannel(): void {
  for (const t of pending.values()) clearTimeout(t);
  pending.clear();
  if (sweepTimer) {
    clearInterval(sweepTimer);
    sweepTimer = null;
  }
  if (dataSocket) {
    try {
      for (const path of myLocks.keys()) sendCode({ t: 'UNLOCK', path });
      if (codeChannel && selfId)
        dataSocket.emit('unsubscribeChannel', { channel: codeChannel, sender: selfId });
    } catch {
      /* best-effort */
    }
    if (onMessageHandler) dataSocket.off('message', onMessageHandler);
    if (onJoinHandler) dataSocket.off('joinChannel', onJoinHandler);
    if (onLeaveHandler) dataSocket.off('leaveChannel', onLeaveHandler);
  }
  onMessageHandler = null;
  onJoinHandler = null;
  onLeaveHandler = null;
  dataSocket = null;
  codeChannel = null;
  participants.clear();
  lastApplied.clear();
  peerLocks.clear();
  myLocks.clear();
}

// ── Control-plane lifecycle (always-on once initialized) ──────────────────────────
function attachCtl(): void {
  if (ctlAttached) return;
  const s = getTunnelSocket();
  if (!s) {
    // Presence hasn't connected yet — poll until the shared socket exists.
    if (!ctlAttachRetry) ctlAttachRetry = setInterval(attachCtl, 500);
    return;
  }
  if (ctlAttachRetry) {
    clearInterval(ctlAttachRetry);
    ctlAttachRetry = null;
  }
  ctlSocket = s;
  ctlHandler = (data) => {
    const ch = getPresenceChannel();
    if (!ch || data?.channel !== ch || !data?.sender || data.sender === selfId) return;
    handleCtl(data.sender, data.message);
  };
  s.on('message', ctlHandler);
  ctlAttached = true;
}

/** Set identity + app and attach the always-on control plane. Idempotent. */
export function initCollab(appId: string, id: string, name: string): void {
  collabAppId = appId;
  selfId = id;
  selfName = name || selfName;
  attachCtl();
}

/** Detach the control plane (call on editor unmount / app switch). */
export function teardownCollab(): void {
  if (phase !== 'idle') leaveLive();
  if (ctlAttachRetry) {
    clearInterval(ctlAttachRetry);
    ctlAttachRetry = null;
  }
  if (ctlSocket && ctlHandler) ctlSocket.off('message', ctlHandler);
  ctlSocket = null;
  ctlHandler = null;
  ctlAttached = false;
}

// ── Session verbs ────────────────────────────────────────────────────────────────

/** Guest: request to join `host`'s live session (freezes until approve/deny).
 *  `stash` = the door choice: keep pre-join work as a draft (true) or discard it
 *  (false) when the host's state is adopted. */
export function requestToJoin(
  appId: string,
  id: string,
  name: string,
  host: { id: string; name: string },
  stash = false,
): void {
  initCollab(appId, id, name);
  if (phase !== 'idle') return;
  stashOnAdopt = stash;
  phase = 'requesting';
  hostId = host.id;
  statusText = `Waiting for ${(host.name || 'host').split(' ')[0]} to approve…`;
  emitState();
  const send = () => {
    if (phase === 'requesting') sendCtl({ t: 'REQUEST_JOIN', name: selfName });
  };
  send();
  clearRequestTimers();
  requestRetry = setInterval(send, REQUEST_RETRY_MS);
  requestTimeout = setTimeout(() => {
    if (phase === 'requesting') {
      clearRequestTimers();
      phase = 'idle';
      hostId = null;
      statusText = 'No response — try again';
      emitState();
      setTimeout(() => {
        if (phase === 'idle') {
          statusText = null;
          emitState();
        }
      }, 3000);
    }
  }, REQUEST_TIMEOUT_MS);
}

/** Request to join a peer using the identity the always-on control plane already
 *  holds (set by initCollab on editor mount). Thin wrapper so callers that don't
 *  carry the self id/name — e.g. the PresenceStack "Join session" action — can
 *  request without re-deriving identity (which risks a selfId mismatch). */
export function requestJoin(host: { id: string; name: string }, stash = false): void {
  if (!collabAppId || !selfId) return; // control plane not initialized yet
  requestToJoin(collabAppId, selfId, selfName, host, stash);
}

/** Host: approve the pending joiner — freeze both and transfer the snapshot. */
export function approveJoin(): void {
  if (!pendingJoin || (phase !== 'hosting' && phase !== 'idle')) return;
  // Approving IMPLICITLY makes us the host if we weren't already (idle) — the
  // session begins the moment you admit the first joiner, so there's no separate
  // "Go live" step. Already-hosting just adds another participant via the exact
  // same transfer path below.
  const wasIdle = phase === 'idle';
  const to = pendingJoin.id;
  // Set the streaming phase FIRST: openCodeChannel()'s subscribe is gated on
  // onCodeChannel() (hosting/transferring/guesting), so opening it while still
  // 'idle' would skip the subscribeChannel — the host would never join the code
  // room and never RECEIVE the guest's edits (bidirectional stream broken).
  phase = 'transferring';
  if (wasIdle) {
    participants.clear();
    openCodeChannel();
  }
  emitState();
  sendCtl({ t: 'JOIN_APPROVED', to });
  sendCtl({
    t: 'SNAPSHOT',
    to,
    kind: 'join',
    files: readFiles(),
    assets: readAssets(),
    baseSaveId: getBaseSaveId(),
  });
  if (transferTimeout) clearTimeout(transferTimeout);
  transferTimeout = setTimeout(() => {
    if (phase === 'transferring') {
      phase = 'hosting';
      pendingJoin = null;
      transferTimeout = null;
      emitState();
    }
  }, TRANSFER_TIMEOUT_MS);
}

/** Host: decline the pending joiner. */
export function denyJoin(): void {
  if (!pendingJoin || (phase !== 'hosting' && phase !== 'idle')) return;
  sendCtl({ t: 'JOIN_DENIED', to: pendingJoin.id });
  pendingJoin = null;
  emitState(); // idle stays idle; hosting stays hosting — we just cleared the ask
}

/** The host ended the session while we were guesting → tear down like leaveLive,
 *  but surface a transient note. Additive: the existing per-participant leave
 *  logic and manual leaveLive are untouched. */
function hostEndedSession(): void {
  closeCodeChannel();
  clearRequestTimers();
  if (transferTimeout) {
    clearTimeout(transferTimeout);
    transferTimeout = null;
  }
  phase = 'idle';
  pendingJoin = null;
  hostId = null;
  statusText = 'Host ended the session';
  emitState();
  // Clear the transient note after a few seconds (mirrors JOIN_DENIED).
  setTimeout(() => {
    if (phase === 'idle') {
      statusText = null;
      emitState();
    }
  }, 4000);
}

/** Leave the session (stop streaming). Keeps the control plane attached so
 *  pull-once still works afterwards. */
export function leaveLive(): void {
  closeCodeChannel();
  clearRequestTimers();
  if (transferTimeout) {
    clearTimeout(transferTimeout);
    transferTimeout = null;
  }
  phase = 'idle';
  pendingJoin = null;
  statusText = null;
  hostId = null;
  emitState();
}

/** One-shot pull: ask `peerId` for their buffer. Resolves with their snapshot
 *  (caller runs the reconcile modal) or null on timeout. No streaming, no lock.
 *  Relies on the always-on control plane (initCollab, done on editor mount). */
export function pullFromPeer(peerId: string): Promise<PulledSnapshot | null> {
  // Only one pull in flight; supersede any previous.
  if (pullResolver) {
    const prev = pullResolver;
    pullResolver = null;
    clearPullTimers();
    prev(null);
  }
  return new Promise<PulledSnapshot | null>((resolve) => {
    pullResolver = resolve;
    const send = () => {
      if (pullResolver) sendCtl({ t: 'REQUEST_SNAPSHOT', to: peerId });
    };
    send();
    pullRetry = setInterval(send, PULL_RETRY_MS);
    pullTimeout = setTimeout(() => {
      if (pullResolver) {
        const r = pullResolver;
        pullResolver = null;
        clearPullTimers();
        r(null);
      }
    }, PULL_TIMEOUT_MS);
  });
}

// ── Edit streaming (data plane) ───────────────────────────────────────────────────
/** Claim/refresh a single-writer lock on a file being edited locally. */
export function claimLock(path: string): void {
  if (!canStream()) return;
  const now = Date.now();
  const held = myLocks.get(path);
  myLocks.set(path, { lastEdit: now, lastSent: held?.lastSent ?? 0 });
  const entry = myLocks.get(path)!;
  if (now - entry.lastSent >= LOCK_REFRESH_MS) {
    entry.lastSent = now;
    sendCode({ t: 'LOCK', path, name: selfName });
  }
}

/** True if we're streaming AND this is a genuine local edit (not an applied echo). */
export function shouldEmit(path: string, contents: string): boolean {
  if (!canStream()) return false;
  return lastApplied.get(path) !== cyrb53(contents);
}

/** Broadcast a local file edit (debounced per path). `contents: null` = delete. */
export function emitEdit(path: string, contents: string | null, fileType = 'CODE'): void {
  if (!canStream()) return;
  const prev = pending.get(path);
  if (prev) clearTimeout(prev);
  pending.set(
    path,
    setTimeout(() => {
      pending.delete(path);
      sendCode({ t: 'EDIT', path, contents, fileType });
    }, EDIT_DEBOUNCE_MS),
  );
}

// ── Seams ─────────────────────────────────────────────────────────────────────────
export function setApply(fn: ApplyFn | null): void {
  applyFn = fn;
}
export function setGetFiles(fn: GetFilesFn | null): void {
  getFilesFn = fn;
}
/** Register the buffer ASSET-pointer reader (used to attach assets to snapshots). */
export function setGetAssets(
  fn: (() => Record<string, { type: string; contents: string }>) | null,
): void {
  getAssetsFn = fn;
}
/** Register the asset-apply hook run on a pull/adopt (peer's ASSET pointers). */
export function setOnMergeAssets(
  fn: ((wireAssets: Record<string, { type: string; contents: string }>) => void) | null,
): void {
  onMergeAssets = fn;
}
/** Register the pre-adopt stash/discard hook (receives the door choice). */
export function setOnBeforeAdopt(fn: ((stash: boolean) => void) | null): void {
  onBeforeAdopt = fn;
}

// ── Introspection ────────────────────────────────────────────────────────────────
/** True while streaming (App.tsx gates edit emission on this). */
export function isLive(): boolean {
  return canStream();
}

/** Subscribe to session-state changes. Fires once immediately. */
export function subscribeCollab(listener: Listener): () => void {
  listeners.add(listener);
  try {
    listener(snapshot());
  } catch {
    /* swallow */
  }
  return () => {
    listeners.delete(listener);
  };
}
